import api from "../../../services/api";
import { toast } from "react-toastify";
import { each, size, sumBy } from "lodash";

const loadData = async (url) => {
  return api
    .get(url)
    .then((response) => {
      if (response && (response.status === 201 || response.status === 200)) {
        return response.data;
      }
      if (!response) {
        toast.error("Erro ao buscar os dados.");
        throw "empty response";
      }
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message);
      } else {
        toast.error("Erro ao buscar os dados.");
      }
      throw err;
    });
};

const loadDataVendaDiretaPost = async (data) => {
  return api
    .post(`/api/front-credito/venda-direta`, { ...data })
    .then((response) => {
      if (response && (response.status === 201 || response.status === 200)) {
        const {
          data: { content, total },
        } = response;
        return response.data;
      }
      if (!response) {
        toast.error("Erro ao buscar os dados.");
      }
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message);
      } else {
        toast.error("Erro ao buscar os dados.");
      }
    });
};

const loadDataNotaFiscalPost = async (data) => {
  return api
    .post(`/api/front-credito/nota-fiscal`, { ...data })
    .then((response) => {
      if (response && (response.status === 201 || response.status === 200)) {
        const {
          data: { content, total },
        } = response;
        return response.data;
      }
      if (!response) {
        toast.error("Erro ao buscar os dados.");
      }
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message);
      } else {
        toast.error("Erro ao buscar os dados.");
      }
    });
};

const loadOrderSummaryAndPaymentTypes = ({ proposalNumber }, setters) => {
  const {
    setOrderSummary,
    setOrderSummaryLoading,
    setPaymentTypes,
    setPaymentTypesLoading,
    setOperationTotalValue,
  } = setters;
  setOrderSummaryLoading(true);
  loadData(
    `api/front-credito/proposta-cadastro-completo?propostaId=${proposalNumber}`
  )
    .then((data) => {
      setOrderSummary(data);
      const isFandi = data?.sistemaOrigem?.toUpperCase() === "FANDI";

      if (!data.numeroPedido) {
        setOperationTotalValue(
          isFandi
            ? data?.valorVeiculoFandi ?? data?.valorProposta
            : data?.valorProposta
        );
      } else {
        setPaymentTypesLoading(true);
        loadData(
          `api/front-credito/forma-pagamento?pedidoId=${data.numeroPedido}`
        )
          .then((paymentData) => {
            setPaymentTypes(paymentData);
            setOperationTotalValue(
              isFandi
                ? data?.valorVeiculoFandi
                : sumBy(paymentData?.formasDePagamento ?? [], "valorTotal") ||
                    data?.valorProposta
            );
          })
          .catch(() => {
            setPaymentTypes();
            setOperationTotalValue(
              isFandi
                ? data?.valorVeiculoFandi ?? data?.valorProposta
                : undefined
            );
          })
          .finally(() => setPaymentTypesLoading(false));
      }
    })
    .catch(() => setOrderSummary())
    .finally(() => setOrderSummaryLoading(false));
};

const loadProposalOperation = ({ proposalNumber }, setters) => {
  const {
    setProposalOperation,
    setProposalOperationLoading,
    setProposalInfoWarranty,
    setProposalInfoWarrantyLoading,
  } = setters;
  setProposalInfoWarrantyLoading(true);
  setProposalInfoWarrantyLoading(true);
  loadData(
    `api/front-credito/dados-operacao-info-garantia?numeroProposta=${proposalNumber}`
  )
    .then(({ dadosOperacao, infoGarantia }) => {
      setProposalOperation(dadosOperacao);
      setProposalInfoWarranty(infoGarantia);
    })
    .catch(() => {
      setProposalOperation();
      setProposalInfoWarranty();
    })
    .finally(() => {
      setProposalOperationLoading(false);
      setProposalInfoWarrantyLoading(false);
    });
};

const loadFluxoConsolidado = ({ cpfCnpj }, setters) => {
  const { setProposalFluxoConsolidado, setProposalFluxoConsolidadoLoading } =
    setters;
  setProposalFluxoConsolidadoLoading(true);
  loadData(`api/front-credito/fluxo-consolidado?cpfCnpj=${cpfCnpj}`)
    .then((data) => setProposalFluxoConsolidado(data))
    .catch(() => setProposalFluxoConsolidado())
    .finally(() => setProposalFluxoConsolidadoLoading(false));
};

const loadConsultaSCR = ({ cpfCnpj }, setters) => {
  const { setProposalConsultaSCR, setProposalConsultaSCRLoading } = setters;
  setProposalConsultaSCRLoading(true);
  loadData(`api/front-credito/consulta-scr-bacen?cpfCnpj=${cpfCnpj}`)
    .then((data) => setProposalConsultaSCR(data))
    .catch(() => setProposalConsultaSCR())
    .finally(() => setProposalConsultaSCRLoading(false));
};

const loadFluxoModalidade = ({ cpfCnpj }, setters) => {
  const { setProposalFluxoModalidade, setProposalFluxoModalidadeLoading } =
    setters;
  setProposalFluxoModalidadeLoading(true);
  loadData(`api/front-credito/fluxo-modalidade?cpfCnpj=${cpfCnpj}`)
    .then((data) => setProposalFluxoModalidade(data))
    .catch(() => setProposalFluxoModalidade())
    .finally(() => setProposalFluxoModalidadeLoading(false));
};

const loadAggregates = ({ proposalNumber }, setters) => {
  const { setAggregates, setAggregatesLoading } = setters;
  setAggregatesLoading(true);
  loadData(`/api/front-credito/agregados?propostaId=${proposalNumber}`)
    .then((data) => setAggregates(data))
    .catch(() => setAggregates())
    .finally(() => setAggregatesLoading(false));
};

const loadProposalCompleteRegistration = ({ proposalNumber }, setters) => {
  const {
    setProposalCompleteRegistration,
    setProposalCompleteRegistrationLoading,
  } = setters;
  setProposalCompleteRegistrationLoading(true);
  loadData(
    `/api/front-credito/proposta-cadastro-completo?propostaId=${proposalNumber}`
  )
    .then((data) => setProposalCompleteRegistration(data))
    .catch(() => setProposalCompleteRegistration())
    .finally(() => setProposalCompleteRegistrationLoading(false));
};
const loadProposalPartnershipRegistrations = ({ cpfCnpj }, setters) => {
  const {
    setProposalPartnershipRegistrations,
    setProposalPartnershipRegistrationsLoading,
  } = setters;
  setProposalPartnershipRegistrationsLoading(true);
  loadData(`/api/front-credito/proposta-cadastro-socios?cpfCnpj=${cpfCnpj}`)
    .then((data) => setProposalPartnershipRegistrations(data))
    .catch(() => setProposalPartnershipRegistrations())
    .finally(() => setProposalPartnershipRegistrationsLoading(false));
};

const loadParticipacoesOutrasEmpresas = ({ cpfCnpj }, setters) => {
  const {
    setParticipacoesOutrasEmpresas,
    setParticipacoesOutrasEmpresasLoading,
  } = setters;
  setParticipacoesOutrasEmpresasLoading(true);
  loadData(
    `/api/front-credito/cliente-participacoes-outras-empresas?cpfCnpj=${cpfCnpj}`
  )
    .then((data) => setParticipacoesOutrasEmpresas(data))
    .catch(() => setParticipacoesOutrasEmpresas())
    .finally(() => setParticipacoesOutrasEmpresasLoading(false));
};

const loadClienteContas = ({ cpfCnpj }, setters) => {
  const { setClienteContas, setClienteContasLoading } = setters;
  setClienteContasLoading(true);
  loadData(`/api/front-credito/cliente-contas?cpfCnpj=${cpfCnpj}`)
    .then((data) => setClienteContas(data))
    .catch(() => setClienteContas())
    .finally(() => setClienteContasLoading(false));
};

const loadVendaDireta = (filters, setters) => {
  const {
    setVendaDireta,
    setVendaDiretaLoading,
    setVendaDiretaTotal,
    setPageVendaDireta,
  } = setters;
  setVendaDiretaLoading(true);
  loadDataVendaDiretaPost(filters)
    .then((data) => {
      setPageVendaDireta(data);
      setVendaDireta(data.content);
      setVendaDiretaTotal(
        data.content.map((a) => a.valorTotal).reduce((a, b) => a + b, 0)
      );
    })
    .catch(() => setVendaDireta(), setVendaDiretaTotal())
    .finally(() => setVendaDiretaLoading(false));
};

const loadClienteAtrasoFinanceira = ({ cpfCnpj }, setters) => {
  const { setClienteAtrasoFinanceira, setClienteAtrasoFinanceiraLoading } =
    setters;
  setClienteAtrasoFinanceiraLoading(true);
  loadData(`/api/front-credito/cliente-atraso-financeira?cpfCnpj=${cpfCnpj}`)
    .then((data) => setClienteAtrasoFinanceira(data))
    .catch(() => setClienteAtrasoFinanceira())
    .finally(() => setClienteAtrasoFinanceiraLoading(false));
};

const loadNotaFiscal = (filters, setters) => {
  const {
    setNotaFiscal,
    setNotaFiscalLoading,
    setNotaFiscalTotal,
    setPageNotaFiscal,
  } = setters;

  setNotaFiscalLoading(true);
  loadDataNotaFiscalPost(filters)
    .then((data) => {
      setPageNotaFiscal(data);
      setNotaFiscal(data.content);
      setNotaFiscalTotal(data.content[0].valorTotalCliente);
    })
    .catch(() => {
      setNotaFiscal();
      setNotaFiscalTotal();
    })
    .finally(() => setNotaFiscalLoading(false));
};

const loadOpinionTypes = (setters) => {
  const { setOpinionTypes, setOpinionTypesLoading } = setters;
  setOpinionTypesLoading(true);
  loadData(`/api/front-credito/tipos-parecer`)
    .then((data) => setOpinionTypes(data))
    .catch(() => setOpinionTypes())
    .finally(() => setOpinionTypesLoading(false));
};

const loadOpinions = ({ proposalNumber }, setters) => {
  const { setOpinions, setOpinionsLoading } = setters;
  setOpinionsLoading(true);
  loadData(`/api/front-credito/pareceres?numeroProposta=${proposalNumber}`)
    .then((data) => setOpinions(data))
    .catch(() => setOpinions())
    .finally(() => setOpinionsLoading(false));
};

const loadProposalPossibleStatuses = ({ proposalNumber }, setters) => {
  const { setProposalPossibleStatuses, setProposalPossibleStatusesLoading } =
    setters;
  setProposalPossibleStatusesLoading(true);
  loadData(
    `/api/front-credito/status-possiveis-proposta?numeroProposta=${proposalNumber}`
  )
    .then((data) => setProposalPossibleStatuses(data))
    .catch(() => setProposalPossibleStatuses())
    .finally(() => setProposalPossibleStatusesLoading(false));
};

const loadEventTypes = (setters) => {
  const { setEventTypes, setEventTypesLoading } = setters;
  setEventTypesLoading(true);
  loadData(`/api/front-credito/tipos-evento`)
    .then((data) => setEventTypes(data))
    .catch(() => setEventTypes())
    .finally(() => setEventTypesLoading(false));
};

const loadEventTypesV2 = (setters) => {
  const { setEventTypes, setEventTypesLoading } = setters;
  setEventTypesLoading(true);
  loadData(`/api/front-credito/tipos-evento-v2`)
    .then((data) => setEventTypes(data))
    .catch(() => setEventTypes())
    .finally(() => setEventTypesLoading(false));
};

const loadEvents = ({ cpfCnpj }, setters) => {
  const { setEvents, setEventsLoading } = setters;
  setEventsLoading(true);
  loadData(`/api/front-credito/eventos?cpfCnpj=${cpfCnpj}`)
    .then((data) => setEvents(data))
    .catch(() => setEvents())
    .finally(() => setEventsLoading(false));
};

const loadEventsV2 = ({ cpfCnpj }, setters) => {
  const { setEvents, setEventsLoading } = setters;
  setEventsLoading(true);
  loadData(`/api/front-credito/eventos-v2?cpfCnpj=${cpfCnpj}`)
    .then((data) => setEvents(data))
    .catch(() => setEvents())
    .finally(() => setEventsLoading(false));
};

const loadProposalWarrantyVehicleInfo = ({ proposalNumber }, setters) => {
  const {
    setProposalWarrantyVehicleInfo,
    setProposalWarrantyVehicleInfoLoading,
  } = setters;
  setProposalWarrantyVehicleInfoLoading(true);
  loadData(
    `api/front-credito/informacoes-veiculo?numeroProposta=${proposalNumber}`
  )
    .then((data) => setProposalWarrantyVehicleInfo(data))
    .catch(() => setProposalWarrantyVehicleInfo())
    .finally(() => setProposalWarrantyVehicleInfoLoading(false));
};

const loadPoliticaCdcVeiculo = ({ proposalNumber }, setters) => {
  const { setPoliticaCdcVeiculo, setPoliticaCdcVeiculoLoading } = setters;
  setPoliticaCdcVeiculoLoading(true);
  loadData(
    `api/front-credito/politica-cdc-veiculo?numeroProposta=${proposalNumber}`
  )
    .then((data) => setPoliticaCdcVeiculo(data))
    .catch(() => setPoliticaCdcVeiculo())
    .finally(() => setPoliticaCdcVeiculoLoading(false));
};

const loadResumoPropostas = ({ cpfCnpj }, setters) => {
  const { setResumoPropostas, setResumoPropostasLoading } = setters;
  setResumoPropostasLoading(true);
  loadData(`api/front-credito/resumo-propostas?cpfCnpj=${cpfCnpj}`)
    .then((data) => setResumoPropostas(data))
    .catch(() => setResumoPropostas())
    .finally(() => setResumoPropostasLoading(false));
};

const loadProposalWarrantyGuarantors = ({ proposalNumber }, setters) => {
  const {
    setProposalWarrantyGuarantors,
    setProposalWarrantyGuarantorsLoading,
    setProposalWarrantGuarantorsOtherCompanies,
    setProposalWarrantGuarantorsCustomerAccounts,
  } = setters;
  setProposalWarrantyGuarantorsLoading(true);
  loadData(
    `api/front-credito/proposta-cadastro-completo-avalistas?numeroProposta=${proposalNumber}`
  )
    .then((guarantors) => {
      const guarantorsSize = size(guarantors);
      const otherCompanies = [guarantorsSize];
      const customerAccounts = [guarantorsSize];
      each(guarantors, (guarantor, index) => {
        loadData(
          `/api/front-credito/cliente-participacoes-outras-empresas?cpfCnpj=${guarantor.cpfCnpj}`
        )
          .then((company) => (otherCompanies[index] = company))
          .catch(() => (otherCompanies[index] = []));
        loadData(
          `/api/front-credito/cliente-contas?cpfCnpj=${guarantor.cpfCnpj}`
        )
          .then((account) => (customerAccounts[index] = account))
          .catch(() => (customerAccounts[index] = []));
      });
      setProposalWarrantGuarantorsOtherCompanies(otherCompanies);
      setProposalWarrantGuarantorsCustomerAccounts(customerAccounts);
      setProposalWarrantyGuarantors(guarantors);
    })
    .catch(() => setProposalWarrantyGuarantors())
    .finally(() => setProposalWarrantyGuarantorsLoading(false));
};

const loadAnotherOrderValues = ({ proposalNumber }, setters) => {
  const { setAnotherOrderValues, setAnotherOrderValuesLoading } = setters;
  setAnotherOrderValuesLoading(true);
  loadData(`api/outros/valores/pedido/${proposalNumber}`)
    .then((data) => setAnotherOrderValues(data))
    .catch(() => setAnotherOrderValues())
    .finally(() => setAnotherOrderValuesLoading(false));
};

const loadVehiclesOnExchange = ({ proposalNumber }, setters) => {
  const { setVehiclesOnExchange, setVehiclesOnExchangeLoading } = setters;
  setVehiclesOnExchangeLoading(true);
  loadData(`api/veiculos/troca/${proposalNumber}`)
    .then((data) => setVehiclesOnExchange(data ? [data] : []))
    .catch(() => setVehiclesOnExchange())
    .finally(() => setVehiclesOnExchangeLoading(false));
};

const loadRejectionOpinions = ({ proposalNumber }, setters) => {
  const { setRejectionOpinions, setRejectionOpinionsLoading } = setters;
  setRejectionOpinionsLoading(true);
  loadData(
    `/api/front-credito/motivos-rejeicao-detalhados?propostaId=${proposalNumber}`
  )
    .then((data) => setRejectionOpinions(data))
    .catch(() => setRejectionOpinions([]))
    .finally(() => setRejectionOpinionsLoading(false));
};

export {
  loadOrderSummaryAndPaymentTypes,
  loadProposalOperation,
  loadFluxoConsolidado,
  loadConsultaSCR,
  loadFluxoModalidade,
  loadAggregates,
  loadProposalCompleteRegistration,
  loadProposalPartnershipRegistrations,
  loadParticipacoesOutrasEmpresas,
  loadClienteContas,
  loadVendaDireta,
  loadClienteAtrasoFinanceira,
  loadNotaFiscal,
  loadOpinionTypes,
  loadOpinions,
  loadProposalPossibleStatuses,
  loadEventTypes,
  loadEventTypesV2,
  loadEvents,
  loadEventsV2,
  loadProposalWarrantyVehicleInfo,
  loadPoliticaCdcVeiculo,
  loadResumoPropostas,
  loadProposalWarrantyGuarantors,
  loadAnotherOrderValues,
  loadVehiclesOnExchange,
  loadRejectionOpinions,
};
